import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
    intl,
    DmsAltBox,
    DmsPage,
    DmsButton,
    fetchData,
    LanguageBaseContext,
} from "dmsbasereact";
import {
    Typography,
    IconButton,
    Tooltip,
    Box,
    CircularProgress,
    Card,
    CardContent,
    Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import Swal from "sweetalert2";
import { APP_ROUTES } from "../../utils/NamedRoutes";
import { useFormatters } from "../../hooks/useFormater";
import { AnnotationFormModal } from "./AnnotationFormModal";
import { AnnotationViewModal } from "./AnnotationViewModal";
import { STATUS_COLORS, FINAL_STATUSES } from "./constants";

export const SystemNoticeDetailPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { noticePk } = useParams();
    const { language } = useContext(LanguageBaseContext);
    const { formatPhone, formatDateTime } = useFormatters();

    const [noticeData, setNoticeData] = useState({
        code: location.state?.noticeCode || "",
        institutionName: location.state?.institutionName || "",
        userName: location.state?.userName || "",
        institutionEmail: location.state?.institutionEmail || "",
        institutionPhone: location.state?.institutionPhone || "",
        userEmail: location.state?.userEmail || "",
        userPhone: location.state?.userPhone || "",
    });
    const [loading, setLoading] = useState(!location.state?.noticeCode);

    const [annotations, setAnnotations] = useState([]);
    const [annotationsLoading, setAnnotationsLoading] = useState(true);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedAnnotation, setSelectedAnnotation] = useState(null);
    const [selectedSequentialNumber, setSelectedSequentialNumber] = useState(null);
    const [statusList, setStatusList] = useState([]);

    useEffect(() => {
        const fetchStatuses = async () => {
            try {
                const response = await fetchData("/system_notice/statuses", "GET", true);
                const data = response?.data?.data || response?.data || [];
                setStatusList(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error("Failed to fetch statuses:", error);
            }
        };
        fetchStatuses();
    }, [language]);

    const statusLabels = useMemo(() => {
        const map = {};
        statusList.forEach((item) => {
            map[item.value] = item.translated_label || item.name;
        });
        return map;
    }, [statusList]);

    const statusOptions = useMemo(() =>
        statusList.map((item) => ({
            value: item.value,
            label: item.translated_label || item.name,
        })),
    [statusList]);

    useEffect(() => {
        if (!location.state?.noticeCode) {
            const fetchNotice = async () => {
                try {
                    const response = await fetchData(
                        `/system_notice/${noticePk}`,
                        "GET",
                        true
                    );
                    const data = response?.data?.data || response?.data;
                    if (data) {
                        setNoticeData({
                            code: data.code || "",
                            institutionName: data.institution?.name || "",
                            userName: data.user?.user_name || "",
                            institutionEmail: data.institution?.email_address || "",
                            institutionPhone: data.institution?.phone_number || "",
                            userEmail: data.user?.user_email || "",
                            userPhone: data.user?.user_phone || "",
                        });
                    }
                } catch (error) {
                    console.error("Failed to fetch notice:", error);
                }
                setLoading(false);
            };
            fetchNotice();
        }
    }, [noticePk, location.state]);

    const loadAnnotations = useCallback(async () => {
        setAnnotationsLoading(true);
        try {
            const response = await fetchData(
                `/notice_annotation/by-notice/${noticePk}`,
                "GET",
                true
            );
            const data = response?.data?.data || response?.data || [];
            const list = Array.isArray(data) ? data : [];
            list.sort((a, b) => b.pk - a.pk);
            setAnnotations(list);
        } catch (error) {
            console.error("Failed to load annotations:", error);
        }
        setAnnotationsLoading(false);
    }, [noticePk]);

    useEffect(() => {
        loadAnnotations();
    }, [loadAnnotations]);

    const handleGoBack = useCallback(() => {
        navigate(APP_ROUTES.SystemNotice);
    }, [navigate]);

    const handleCreateSaved = useCallback(() => {
        setCreateModalOpen(false);
        loadAnnotations();
    }, [loadAnnotations]);

    const handleEditSaved = useCallback(() => {
        setEditModalOpen(false);
        setSelectedAnnotation(null);
        loadAnnotations();
    }, [loadAnnotations]);

    const handleView = useCallback((annotation, seqNumber) => {
        setSelectedAnnotation(annotation);
        setSelectedSequentialNumber(seqNumber);
        setViewModalOpen(true);
    }, []);

    const handleEdit = useCallback((annotation) => {
        setSelectedAnnotation(annotation);
        setEditModalOpen(true);
    }, []);

    const handleDelete = useCallback(async (annotation) => {
        const result = await Swal.fire({
            icon: "warning",
            title: intl.get("ConfirmDeleteAnnotation").d("Delete this annotation?"),
            showCancelButton: true,
            confirmButtonText: intl.get("Yes").d("Yes"),
            cancelButtonText: intl.get("No").d("No"),
            confirmButtonColor: "#d33",
        });

        if (!result.isConfirmed) return;

        try {
            await fetchData(`/notice_annotation/${annotation.pk}`, "DELETE", true);
            loadAnnotations();
        } catch (error) {
            console.error("Failed to delete annotation:", error);
        }
    }, [loadAnnotations]);

    const contactTooltipContent = useMemo(() => {
        const items = [];
        if (noticeData.institutionEmail) {
            items.push({ label: intl.get("InstitutionEmail").d("Institution Email"), value: noticeData.institutionEmail });
        }
        if (noticeData.institutionPhone) {
            items.push({ label: intl.get("InstitutionPhone").d("Institution Phone"), value: formatPhone(noticeData.institutionPhone) });
        }
        if (noticeData.userEmail) {
            items.push({ label: intl.get("UserEmail").d("User Email"), value: noticeData.userEmail });
        }
        if (noticeData.userPhone) {
            items.push({ label: intl.get("UserPhone").d("User Phone"), value: formatPhone(noticeData.userPhone) });
        }
        return items;
    }, [noticeData, language, formatPhone]);

    const getAnnotationAuthor = useCallback((annotation) => {
        if (annotation.updated_by && annotation.updated_by.user_name) {
            return annotation.updated_by.user_name;
        }
        if (annotation.created_by && annotation.created_by.user_name) {
            return annotation.created_by.user_name;
        }
        return "-";
    }, []);

    if (loading) {
        return (
            <DmsPage name="SystemNoticeDetail">
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                    <CircularProgress />
                </Box>
            </DmsPage>
        );
    }

    return (
        <DmsPage name="SystemNoticeDetail">
            <DmsAltBox.Card>
                <DmsAltBox.Header>
                    <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                    }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Tooltip title={intl.get("Back").d("Back")}>
                                <IconButton
                                    size="small"
                                    onClick={handleGoBack}
                                    sx={{
                                        color: "primary.main",
                                        backgroundColor: "#fff",
                                        "&:hover": { backgroundColor: "grey.100" },
                                    }}
                                >
                                    <ArrowBackIcon />
                                </IconButton>
                            </Tooltip>
                            <Typography variant="h3" sx={{ fontSize: "1.1rem" }}>
                                {noticeData.code}
                            </Typography>
                            {annotations.length > 0 && (() => {
                                const currentStatus = annotations[0].status?.value !== undefined
                                    ? annotations[0].status.value
                                    : annotations[0].status;
                                return (
                                    <Chip
                                        label={statusLabels[currentStatus] || "-"}
                                        size="small"
                                        sx={{ bgcolor: "rgba(255,255,255,0.25)", color: "#fff", fontWeight: 500 }}
                                    />
                                );
                            })()}
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box sx={{ textAlign: "right" }}>
                                {noticeData.institutionName && (
                                    <Typography variant="body2" fontWeight="500">
                                        {noticeData.institutionName}
                                    </Typography>
                                )}
                                {noticeData.userName && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        {noticeData.userName}
                                    </Typography>
                                )}
                            </Box>
                            <Tooltip
                                title={
                                    <Box sx={{ fontSize: "0.8rem", p: 0.5 }}>
                                        {contactTooltipContent.length > 0 ? contactTooltipContent.map((item, i) => (
                                            <Box key={i} sx={{ mb: i < contactTooltipContent.length - 1 ? 0.5 : 0 }}>
                                                <Typography component="span" sx={{ fontWeight: "bold", fontSize: "inherit" }}>
                                                    {item.label}:
                                                </Typography>{" "}
                                                {item.value}
                                            </Box>
                                        )) : (
                                            <Box>{intl.get("NoContactInfo").d("No contact information available")}</Box>
                                        )}
                                    </Box>
                                }
                                arrow
                                placement="bottom-end"
                                componentsProps={{
                                    tooltip: {
                                        sx: {
                                            bgcolor: "background.paper",
                                            color: "text.primary",
                                            boxShadow: 3,
                                            border: "1px solid",
                                            borderColor: "divider",
                                            "& .MuiTooltip-arrow": {
                                                color: "background.paper",
                                                "&::before": {
                                                    border: "1px solid",
                                                    borderColor: "divider",
                                                },
                                            },
                                        },
                                    },
                                }}
                            >
                                <IconButton size="small" sx={{ color: "#fff" }}>
                                    <InfoOutlinedIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                </DmsAltBox.Header>
                <DmsAltBox.Body>
                    <Box sx={{ p: 3 }}>
                        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                            <DmsButton
                                variant="primary"
                                text={intl.get("AddAnnotation").d("Add Annotation")}
                                onClick={() => setCreateModalOpen(true)}
                            />
                        </Box>

                        {annotationsLoading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                                <CircularProgress size={28} />
                            </Box>
                        ) : annotations.length === 0 ? (
                            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                                {intl.get("NoAnnotationsYet").d("No annotations yet")}
                            </Typography>
                        ) : (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                {annotations.map((annotation, index) => {
                                    const statusValue = annotation.status?.value !== undefined
                                        ? annotation.status.value
                                        : annotation.status;
                                    const isLatest = index === 0;
                                    const isFinal = FINAL_STATUSES.includes(statusValue);
                                    const canEdit = isLatest && !isFinal;
                                    const sequentialNumber = annotations.length - index;

                                    return (
                                        <Card
                                            key={annotation.pk}
                                            variant="outlined"
                                            sx={{
                                                borderLeft: 4,
                                                borderLeftColor: isLatest ? "primary.main" : "grey.300",
                                            }}
                                        >
                                            <CardContent sx={{ pb: "12px !important" }}>
                                                <Box sx={{
                                                    display: "flex",
                                                    alignItems: "flex-start",
                                                    justifyContent: "space-between",
                                                }}>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                                                            <Typography variant="caption" color="text.secondary" fontWeight="bold">
                                                                #{sequentialNumber}
                                                            </Typography>
                                                            <Typography variant="subtitle1" fontWeight="600">
                                                                {annotation.title}
                                                            </Typography>
                                                        </Box>
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            <strong>{intl.get("LastUpdate").d("Last Update")}:</strong> {formatDateTime(annotation.updated_on)}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            <strong>{intl.get("UpdatedBy").d("Updated by")}:</strong> {annotation.updated_by?.user_name || "-"}
                                                        </Typography>
                                                        {annotation.text && (
                                                            <Box sx={{ mt: 1 }}>
                                                            <Typography variant="caption" fontWeight="bold" color="text.secondary">
                                                                {intl.get("Annotation").d("Annotation")}
                                                            </Typography>
                                                            <Box
                                                                sx={{
                                                                    mt: 0.5,
                                                                    p: 1.5,
                                                                    bgcolor: "grey.50",
                                                                    borderRadius: 1,
                                                                    fontSize: "0.8rem",
                                                                    "& p": { m: 0, mb: 0.5 },
                                                                    "& hr": { my: 1, border: "none", borderTop: "1px solid", borderColor: "divider" },
                                                                    "& table": { width: "100%", borderCollapse: "collapse", mt: 1 },
                                                                    "& th, & td": { border: "1px solid #ddd", p: 0.5, fontSize: "0.75rem" },
                                                                }}
                                                                dangerouslySetInnerHTML={{ __html: annotation.text }}
                                                            />
                                                            </Box>
                                                        )}
                                                    </Box>

                                                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5 }}>
                                                        <Chip
                                                            label={statusLabels[statusValue] || "-"}
                                                            size="small"
                                                            color={STATUS_COLORS[statusValue] || "default"}
                                                        />
                                                        {annotation.files && annotation.files.length > 0 && (
                                                            <Tooltip title={`${annotation.files.length} ${annotation.files.length === 1 ? intl.get("File").d("file") : intl.get("Files").d("files")}`}>
                                                                <AttachFileIcon fontSize="small" color="action" />
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                </Box>

                                                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5, mt: 1 }}>
                                                    <Tooltip title={intl.get("ViewAnnotation").d("View")}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleView(annotation, sequentialNumber)}
                                                            color="info"
                                                        >
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {canEdit && (
                                                        <>
                                                            <Tooltip title={intl.get("EditAnnotation").d("Edit")}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleEdit(annotation)}
                                                                    color="primary"
                                                                >
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title={intl.get("DeleteAnnotation").d("Delete")}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleDelete(annotation)}
                                                                    color="error"
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </>
                                                    )}
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </Box>
                        )}
                    </Box>
                </DmsAltBox.Body>
            </DmsAltBox.Card>

            <AnnotationFormModal
                open={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                onSaved={handleCreateSaved}
                noticePk={noticePk}
                annotation={null}
                statusOptions={statusOptions}
                lastStatus={annotations[0]?.status?.value !== undefined ? annotations[0].status.value : annotations[0]?.status}
            />

            <AnnotationFormModal
                open={editModalOpen}
                onClose={() => { setEditModalOpen(false); setSelectedAnnotation(null); }}
                onSaved={handleEditSaved}
                noticePk={noticePk}
                annotation={selectedAnnotation}
                statusOptions={statusOptions}
            />

            <AnnotationViewModal
                open={viewModalOpen}
                onClose={() => { setViewModalOpen(false); setSelectedAnnotation(null); setSelectedSequentialNumber(null); }}
                annotation={selectedAnnotation}
                statusLabels={statusLabels}
                sequentialNumber={selectedSequentialNumber}
            />
        </DmsPage>
    );
};

export default SystemNoticeDetailPage;
